import { Task, TaskType, Priority, Frequency, TaskStatus, CompletionType, WeekDay, RecurrenceInterval } from '../types/task.js';
import { formatLocalDate } from '../lib/dateUtils.js';

/**
 * Utility for mapping between Supabase Database rows and Frontend Task objects.
 * Centralizing this ensures that schema changes only need to be updated in one place.
 */

// SEC-M7 phase 3: explicit column allowlist for `tasks` reads. Replace bare
// `.select('*')` with `.select(TASK_COLUMNS)` so future sensitive columns added
// to the table do not auto-flow to the client. Keep in sync with `dbRowToTask`.
export const TASK_COLUMNS =
  'id, team_id, text, completed, created_at, updated_at, type, priority, priority_value, ' +
  'frequency, recurrence_interval, recurrence_value, recurrence_days, ' +
  'last_completed_date, notes, image_url, created_by, completed_by, assigned_to, ' +
  'due_date, location_id, tag_id, tag_ids, status, progress, flow_id, flow_order, ' +
  'flow_skippable, snoozed_date, replan_upon_completion, escalate_priority_if_overdue, ' +
  'keep_until_done, stock_item_id, meal_plan_id, show_from_hour, ' +
  'estimated_duration_minutes, actual_duration_minutes, worked_duration_minutes, timer_started_at, ' +
  'parent_id, completion_type, price, quantity, quantity_unit, ' +
  'container_quantity, container_unit, webshop_links, trigger_state_id, ' +
  'target_state_id, notification_time, last_notified_at, planned_date, pinned_until';

export const dbRowToTask = (row: any): Task => {
  return {
    id: row.id,
    text: row.text,
    completed: row.completed,
    createdAt: new Date(row.created_at),
    updatedAt: row.updated_at ? new Date(row.updated_at) : undefined,
    type: row.type as TaskType,
    priority: row.priority as Priority,
    priorityValue: row.priority_value,
    frequency: row.frequency as Frequency,
    recurrenceInterval: row.recurrence_interval as RecurrenceInterval | undefined,
    recurrenceValue: row.recurrence_value,
    recurrenceDays: row.recurrence_days as WeekDay[] | undefined,
    lastCompletedDate: row.last_completed_date ? new Date(row.last_completed_date) : null,
    notes: row.notes || '',
    imageUrl: row.image_url || undefined,
    createdBy: row.created_by,
    completedBy: row.completed_by || [],
    assignedTo: row.assigned_to || [],
    dueDate: row.due_date ? new Date(row.due_date) : null,
    locationId: row.location_id,
    tagId: row.tag_id,
    tagIds: row.tag_ids || [],
    status: row.status as TaskStatus,
    progress: row.progress || 0,
    flowId: row.flow_id,
    flowOrder: row.flow_order,
    flowSkippable: row.flow_skippable,
    snoozedDate: row.snoozed_date ? new Date(row.snoozed_date) : null,
    replanUponCompletion: row.replan_upon_completion,
    escalatePriorityIfOverdue: row.escalate_priority_if_overdue,
    keepUntilDone: row.keep_until_done,
    stockItemId: row.stock_item_id,
    mealPlanId: row.meal_plan_id ?? null,
    showFromHour: row.show_from_hour,
    estimatedDurationMinutes: row.estimated_duration_minutes,
    actualDurationMinutes: row.actual_duration_minutes,
    workedDurationMinutes: row.worked_duration_minutes ?? null,
    timerStartedAt: row.timer_started_at ? new Date(row.timer_started_at) : null,
    parentId: row.parent_id,
    completionType: row.completion_type as CompletionType | null,
    price: row.price,
    quantity: row.quantity,
    quantityUnit: row.quantity_unit || 'stuks',
    containerQuantity: row.container_quantity,
    containerUnit: row.container_unit || 'doos',
    webshopLinks: row.webshop_links || [],
    triggerStateId: row.trigger_state_id,
    targetStateId: row.target_state_id,
    notificationTime: row.notification_time,
    lastNotifiedAt: row.last_notified_at ? new Date(row.last_notified_at) : null,
    plannedDate: row.planned_date ? new Date(row.planned_date) : null,
    pinnedUntil: row.pinned_until ? new Date(row.pinned_until) : null,
  };
};

export const mapTaskToDb = (updates: Partial<Task>): any => {
  const dbUpdates: any = {};
  if ('text' in updates) dbUpdates.text = updates.text;
  if ('type' in updates) dbUpdates.type = updates.type;
  if ('priority' in updates) dbUpdates.priority = updates.priority;
  if ('priorityValue' in updates) dbUpdates.priority_value = updates.priorityValue;
  if ('status' in updates) dbUpdates.status = updates.status;
  if ('timerStartedAt' in updates) dbUpdates.timer_started_at = updates.timerStartedAt ? (updates.timerStartedAt instanceof Date ? updates.timerStartedAt.toISOString() : updates.timerStartedAt) : null;
  if ('actualDurationMinutes' in updates) dbUpdates.actual_duration_minutes = updates.actualDurationMinutes;
  if ('workedDurationMinutes' in updates) dbUpdates.worked_duration_minutes = updates.workedDurationMinutes ?? null;
  if ('completed' in updates) dbUpdates.completed = updates.completed;
  if ('lastCompletedDate' in updates) dbUpdates.last_completed_date = updates.lastCompletedDate ? (updates.lastCompletedDate instanceof Date ? updates.lastCompletedDate.toISOString() : updates.lastCompletedDate) : null;
  if ('completedBy' in updates) dbUpdates.completed_by = updates.completedBy;
  if ('assignedTo' in updates) dbUpdates.assigned_to = updates.assignedTo;
  if ('dueDate' in updates) dbUpdates.due_date = updates.dueDate ? (updates.dueDate instanceof Date ? formatLocalDate(updates.dueDate) : updates.dueDate) : null;
  if ('notes' in updates) dbUpdates.notes = updates.notes;
  if ('imageUrl' in updates) dbUpdates.image_url = updates.imageUrl;
  if ('locationId' in updates) dbUpdates.location_id = updates.locationId;
  if ('tagId' in updates) dbUpdates.tag_id = updates.tagId;
  if ('tagIds' in updates) dbUpdates.tag_ids = updates.tagIds;
  if ('flowId' in updates) dbUpdates.flow_id = updates.flowId;
  if ('flowOrder' in updates) dbUpdates.flow_order = updates.flowOrder;
  if ('flowSkippable' in updates) dbUpdates.flow_skippable = updates.flowSkippable;
  if ('frequency' in updates) dbUpdates.frequency = updates.frequency;
  if ('recurrenceInterval' in updates) dbUpdates.recurrence_interval = updates.recurrenceInterval;
  if ('recurrenceValue' in updates) dbUpdates.recurrence_value = updates.recurrenceValue;
  if ('recurrenceDays' in updates) dbUpdates.recurrence_days = updates.recurrenceDays;
  if ('snoozedDate' in updates) dbUpdates.snoozed_date = updates.snoozedDate ? (updates.snoozedDate instanceof Date ? formatLocalDate(updates.snoozedDate) : updates.snoozedDate) : null;
  if ('replanUponCompletion' in updates) dbUpdates.replan_upon_completion = updates.replanUponCompletion;
  if ('escalatePriorityIfOverdue' in updates) dbUpdates.escalate_priority_if_overdue = updates.escalatePriorityIfOverdue;
  if ('keepUntilDone' in updates) dbUpdates.keep_until_done = updates.keepUntilDone;
  if ('showFromHour' in updates) dbUpdates.show_from_hour = updates.showFromHour;
  if ('estimatedDurationMinutes' in updates) dbUpdates.estimated_duration_minutes = updates.estimatedDurationMinutes;
  if ('triggerStateId' in updates) dbUpdates.trigger_state_id = updates.triggerStateId;
  if ('targetStateId' in updates) dbUpdates.target_state_id = updates.targetStateId;
  if ('notificationTime' in updates) dbUpdates.notification_time = updates.notificationTime;
  if ('lastNotifiedAt' in updates) dbUpdates.last_notified_at = updates.lastNotifiedAt ? (updates.lastNotifiedAt instanceof Date ? updates.lastNotifiedAt.toISOString() : updates.lastNotifiedAt) : null;
  if ('webshopLinks' in updates) dbUpdates.webshop_links = updates.webshopLinks;
  if ('stockItemId' in updates) dbUpdates.stock_item_id = updates.stockItemId;
  if ('mealPlanId' in updates) dbUpdates.meal_plan_id = updates.mealPlanId ?? null;
  if ('parentId' in updates) dbUpdates.parent_id = updates.parentId;
  if ('completionType' in updates) dbUpdates.completion_type = updates.completionType;
  if ('price' in updates) dbUpdates.price = updates.price;
  if ('quantity' in updates) dbUpdates.quantity = updates.quantity;
  if ('quantityUnit' in updates) dbUpdates.quantity_unit = updates.quantityUnit;
  if ('containerQuantity' in updates) dbUpdates.container_quantity = updates.containerQuantity;
  if ('containerUnit' in updates) dbUpdates.container_unit = updates.containerUnit;
  if ('plannedDate' in updates) dbUpdates.planned_date = updates.plannedDate ? (updates.plannedDate instanceof Date ? formatLocalDate(updates.plannedDate) : updates.plannedDate) : null;
  if ('pinnedUntil' in updates) dbUpdates.pinned_until = updates.pinnedUntil ? (updates.pinnedUntil instanceof Date ? formatLocalDate(updates.pinnedUntil) : updates.pinnedUntil) : null;

  return dbUpdates;
};
